import React, { useState, useEffect } from "react";
import {
  Card,
  List,
  Button,
  Tag,
  Space,
  Typography,
  Checkbox,
  message,
  Modal,
  Form,
  Input,
  Spin,
  Progress,
  Empty,
  Alert,
  Badge,
  Avatar,
  Timeline,
  Collapse,
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  MessageOutlined,
  UserOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  FlagOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { ITaskList, ITask, IComment } from "../../../interfaces";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;

interface MobileTaskListViewProps {
  token: string;
}

export const MobileTaskListView: React.FC<MobileTaskListViewProps> = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [taskList, setTaskList] = useState<ITaskList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [commentModalVisible, setCommentModalVisible] = useState(false);
  const [commentingTask, setCommentingTask] = useState<ITask | null>(null);
  const [expandedTasks, setExpandedTasks] = useState<string[]>([]);
  const [updatingTaskId, setUpdatingTaskId] = useState<number | null>(null);
  
  const [commentForm] = Form.useForm();

  // TaskList über Token laden
  const loadTaskList = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `/api/tasklist/token/${token}`,
        {
          method: "GET",
          headers: {
            "Accept": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Ungültiger Zugangslink");
      }

      const data = await response.json();
      
      if (data.success) {
        setTaskList(data.data);
      } else {
        throw new Error(data.error || "Fehler beim Laden");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ein Fehler ist aufgetreten");
    } finally {
      setLoading(false);
    }
  };

  // Task-Status aktualisieren
  const updateTaskStatus = async (taskId: number, newStatus: string) => {
    try {
      setUpdatingTaskId(taskId);
      
      const response = await fetch(
        `/api/tasklist/token/${token}/task/${taskId}`,
        {
          method: "PUT",
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status: newStatus }),
        }
      );

      if (!response.ok) {
        throw new Error("Fehler beim Aktualisieren");
      }

      const data = await response.json();
      
      if (data.success) {
        message.success("Status erfolgreich aktualisiert");
        // TaskList neu laden
        loadTaskList();
      } else {
        throw new Error(data.error || "Fehler beim Aktualisieren");
      }
    } catch (err) {
      message.error(err instanceof Error ? err.message : "Fehler beim Aktualisieren");
    } finally {
      setUpdatingTaskId(null);
    }
  };

  // Kommentar hinzufügen
  const handleCommentSubmit = async (values: any) => {
    if (!commentingTask) return;

    try {
      const response = await fetch(
        `/api/tasklist/comment/create/${commentingTask.id}`,
        {
          method: "POST",
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...values,
            author_email: taskList?.assigned_email || "",
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Fehler beim Speichern");
      }

      const data = await response.json();
      
      if (data.success) {
        message.success("Kommentar hinzugefügt");
        setCommentModalVisible(false);
        setCommentingTask(null);
        commentForm.resetFields();
        // TaskList neu laden
        loadTaskList();
      } else {
        throw new Error(data.error || "Fehler beim Speichern");
      }
    } catch (err) {
      message.error(err instanceof Error ? err.message : "Fehler beim Speichern");
    }
  };

  useEffect(() => {
    loadTaskList();
  }, [token]);

  // Status-Tag rendern
  const renderTaskStatus = (status: string) => {
    const statusConfig = {
      pending: { color: "default", icon: <ClockCircleOutlined />, text: "Ausstehend" },
      in_progress: { color: "processing", icon: <SyncOutlined spin />, text: "In Arbeit" },
      completed: { color: "success", icon: <CheckCircleOutlined />, text: "Erledigt" },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;

    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  // Priorität rendern
  const renderPriority = (priority: string) => {
    const priorityConfig = {
      low: { color: "default", text: "Niedrig" },
      normal: { color: "blue", text: "Normal" },
      high: { color: "red", text: "Hoch", icon: <FlagOutlined /> },
    };

    const config = priorityConfig[priority as keyof typeof priorityConfig] || priorityConfig.normal;

    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  // Kommentare rendern
  const renderComments = (comments: IComment[]) => {
    if (!comments || comments.length === 0) return null;

    return (
      <Timeline style={{ marginTop: 16 }}>
        {comments.map((comment) => (
          <Timeline.Item
            key={comment.id}
            color={comment.is_important ? "red" : "gray"}
            dot={comment.requires_response ? <ExclamationCircleOutlined /> : null}
          >
            <div>
              <Space>
                <Avatar icon={<UserOutlined />} size="small" />
                <Text strong>{comment.author_display}</Text>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {comment.created_formatted}
                </Text>
              </Space>
              {comment.is_important && <Tag color="red" style={{ marginLeft: 8 }}>Wichtig</Tag>}
              {comment.requires_response && <Tag color="orange" style={{ marginLeft: 8 }}>Antwort erforderlich</Tag>}
              <Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
                {comment.content}
              </Paragraph>
            </div>
          </Timeline.Item>
        ))}
      </Timeline>
    );
  };

  // Task-Karte rendern
  const renderTaskCard = (task: ITask) => {
    const isExpanded = expandedTasks.includes(task.id.toString());
    const isUpdating = updatingTaskId === task.id;

    return (
      <Card
        key={task.id}
        style={{ marginBottom: 16 }}
        bodyStyle={{ padding: 16 }}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          {/* Task Header */}
          <Space align="start" style={{ width: "100%", justifyContent: "space-between" }}>
            <Title level={5} style={{ margin: 0, flex: 1 }}>
              {task.title}
            </Title>
            <Space>
              {renderPriority(task.priority)}
              {renderTaskStatus(task.status)}
            </Space>
          </Space>

          {/* Task Description */}
          {task.description && (
            <Paragraph type="secondary" style={{ marginBottom: 8 }}>
              {task.description}
            </Paragraph>
          )}

          {/* Task Actions */}
          <Space wrap>
            <Checkbox
              checked={task.status === "completed"}
              onChange={(e) => updateTaskStatus(task.id, e.target.checked ? "completed" : "pending")}
              disabled={isUpdating}
            >
              {isUpdating ? <LoadingOutlined /> : "Erledigt"}
            </Checkbox>

            <Button
              type="link"
              icon={<MessageOutlined />}
              onClick={() => {
                setCommentingTask(task);
                setCommentModalVisible(true);
              }}
              size="small"
            >
              Kommentar
              {task.comment_count > 0 && (
                <Badge count={task.comment_count} style={{ marginLeft: 8 }} />
              )}
            </Button>
          </Space>

          {/* Completion Info */}
          {task.status === "completed" && task.completed_at && (
            <Text type="secondary" style={{ fontSize: "12px" }}>
              <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 4 }} />
              Erledigt am {new Date(task.completed_at * 1000).toLocaleString("de-DE")}
            </Text>
          )}

          {/* Comments Section */}
          {task.comments && task.comments.length > 0 && (
            <Collapse
              ghost
              activeKey={isExpanded ? ["comments"] : []}
              onChange={(keys) => {
                setExpandedTasks(
                  keys.includes("comments")
                    ? [...expandedTasks, task.id.toString()]
                    : expandedTasks.filter((id) => id !== task.id.toString())
                );
              }}
            >
              <Panel
                header={
                  <Space>
                    <MessageOutlined />
                    {task.comments.length} Kommentar(e)
                  </Space>
                }
                key="comments"
              >
                {renderComments(task.comments)}
              </Panel>
            </Collapse>
          )}
        </Space>
      </Card>
    );
  };

  // Loading State
  if (loading) {
    return (
      <div style={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        height: "100vh",
        flexDirection: "column"
      }}>
        <Spin size="large" />
        <Text style={{ marginTop: 16 }}>Aufgabenliste wird geladen...</Text>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          message="Zugriff verweigert"
          description={error}
          type="error"
          showIcon
        />
      </div>
    );
  }

  // No TaskList
  if (!taskList) {
    return (
      <div style={{ padding: 24 }}>
        <Empty description="Keine Aufgabenliste gefunden" />
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: "100vh", 
      backgroundColor: "#f0f2f5",
      paddingBottom: 24
    }}>
      {/* Header */}
      <div style={{ 
        backgroundColor: "#fff", 
        padding: "16px 24px",
        borderBottom: "1px solid #f0f0f0",
        position: "sticky",
        top: 0,
        zIndex: 10
      }}>
        <Title level={4} style={{ margin: 0 }}>
          {taskList.title}
        </Title>
        {taskList.description && (
          <Text type="secondary">{taskList.description}</Text>
        )}
      </div>

      {/* Progress Bar */}
      {taskList.statistics && (
        <div style={{ backgroundColor: "#fff", padding: "16px 24px" }}>
          <Progress
            percent={taskList.statistics.completionRate || 0}
            status={taskList.statistics.completionRate === 100 ? "success" : "active"}
            format={(percent) => 
              `${taskList.statistics.completed} von ${taskList.statistics.total} erledigt`
            }
          />
        </div>
      )}

      {/* Tasks List */}
      <div style={{ padding: "16px" }}>
        {taskList.tasks && taskList.tasks.length > 0 ? (
          <List
            dataSource={taskList.tasks}
            renderItem={(task) => renderTaskCard(task)}
          />
        ) : (
          <Card>
            <Empty description="Keine Aufgaben vorhanden" />
          </Card>
        )}
      </div>

      {/* Comment Modal */}
      <Modal
        title={`Kommentar zu: ${commentingTask?.title}`}
        open={commentModalVisible}
        onCancel={() => {
          setCommentModalVisible(false);
          setCommentingTask(null);
          commentForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={commentForm}
          layout="vertical"
          onFinish={handleCommentSubmit}
        >
          <Form.Item
            name="content"
            label="Ihr Kommentar"
            rules={[{ required: true, message: "Bitte Kommentar eingeben" }]}
          >
            <TextArea 
              rows={4} 
              placeholder="z.B. Reinigungsmittel fehlt, Raum war verschlossen..." 
            />
          </Form.Item>

          <Form.Item name="author_name" label="Ihr Name (optional)">
            <Input placeholder="Max Mustermann" />
          </Form.Item>

          <Form.Item name="is_important" valuePropName="checked">
            <Checkbox>Als wichtig markieren</Checkbox>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Kommentar senden
              </Button>
              <Button onClick={() => setCommentModalVisible(false)}>
                Abbrechen
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};